import type { BleDevice, GattServer } from '@/ble/types';
import { tryGetChar, tryGetService } from '@/ble/gatt';
import { UUID } from '@/ble/uuids';
import type { AnyTrainer, TrainerKind } from '@/trainer/types';
import { FtmsTrainer } from './ftms/FtmsTrainer';
import { WahooLegacyTrainer } from './wahoo/WahooLegacyTrainer';
import { FecTrainer } from './fec/FecTrainer';

export type DetectedKind = Exclude<TrainerKind, 'sim'> | 'power-only' | 'none';

/** SPEC §4.1 detection order: FTMS → FE-C → Wahoo legacy characteristic. */
export function detectTrainerKind(p: {
  ftms: boolean;
  fec: boolean;
  cps: boolean;
  wahooChar: boolean;
}): DetectedKind {
  if (p.ftms) return 'ftms';
  if (p.fec) return 'fec';
  if (p.cps && p.wahooChar) return 'wahoo-legacy';
  if (p.cps) return 'power-only';
  return 'none';
}

export async function probeServer(server: GattServer): Promise<Parameters<typeof detectTrainerKind>[0]> {
  const ftms = !!(await tryGetService(server, UUID.FTMS));
  const fec = !!(await tryGetService(server, UUID.FEC));
  const cpsSvc = await tryGetService(server, UUID.CPS);
  const wahooChar = cpsSvc ? !!(await tryGetChar(cpsSvc, UUID.WAHOO_TRAINER)) : false;
  return { ftms, fec, cps: !!cpsSvc, wahooChar };
}

export class NoControllableTrainerError extends Error {
  constructor(readonly detected: DetectedKind) {
    super(detected === 'power-only' ? 'power-only' : 'no-trainer');
  }
}

/** Connect, probe services and construct the matching trainer implementation. */
export async function createTrainer(device: BleDevice): Promise<AnyTrainer> {
  const gatt = device.gatt;
  if (!gatt) throw new Error('Device has no GATT server');
  const server = gatt.connected ? gatt : await gatt.connect();
  const kind = detectTrainerKind(await probeServer(server));
  switch (kind) {
    case 'ftms':
      return new FtmsTrainer(device);
    case 'fec':
      return new FecTrainer(device);
    case 'wahoo-legacy':
      return new WahooLegacyTrainer(device);
    default:
      throw new NoControllableTrainerError(kind);
  }
}
